import React, { Fragment, memo } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  concatPaymentMethodWithSubType,
  isPaymentMethodWithSubTypeEnabled,
} from 'businessLogic/Payment/helpers';
import { breakpoints, colors, fontSize } from 'styles/cp';
import { Wallet } from 'types/Wallet';
import { PaymentMethod, PaymentMethodSubType } from 'types/constants';

interface PaymentMethodTabProps {
  paymentMethod: PaymentMethod;
  availablePayments: PaymentMethod[];
  selectedPaymentMethod: PaymentMethod;
  onPaymentMethodTypeChange: (type: PaymentMethod, subType?: PaymentMethodSubType) => void;
  Icon: React.ComponentType<{
    width?: number;
    height?: number;
    customStyling?: React.CSSProperties;
  }>;
  formattedMessageId: string;
  defaultMessage: string;
  isMobile?: boolean;
  paymentMethodSubType?: PaymentMethodSubType;
  wallet?: Wallet;
  isPPCheckoutExperiment: boolean;
  surchargeLabel?: string;
  isSurchargingEnabled: boolean;
}

const PaymentMethodTab: React.FC<PaymentMethodTabProps> = (props) => {
  const {
    paymentMethod,
    selectedPaymentMethod,
    onPaymentMethodTypeChange,
    Icon,
    formattedMessageId,
    defaultMessage,
    isMobile,
    paymentMethodSubType,
    wallet,
    isPPCheckoutExperiment,
    surchargeLabel,
    isSurchargingEnabled,
  } = props;

  const handlePaymentMethodTypeChange = (_ev: React.MouseEvent<HTMLLIElement>) => {
    onPaymentMethodTypeChange(paymentMethod, paymentMethodSubType);
  };

  const isPPamIncluded = ['paypal_ppaam'].includes(paymentMethod);

  if (paymentMethodSubType) {
    // Checking if subtype is enabled
    if (wallet && !isPaymentMethodWithSubTypeEnabled({ wallet, paymentMethodSubType })) {
      return <></>;
    }
  }

  const PlaceholderButton = ({ children }: { children: React.ReactNode | string | null }) => {
    return (
      <Fragment>
        {/*language=SCSS*/}
        <style jsx>
          {`
            .payment-method-button {
              border: none;
              background-color: initial;
              display: flex;
              flex-direction: ${isPPamIncluded ? 'row' : 'column'};
              align-items: center;
              margin: auto;
            }
          `}
        </style>
        <button className="payment-method-button">{children}</button>
      </Fragment>
    );
  };
  return (
    <>
      <li
        data-testid={`payment-tab-${paymentMethod}`}
        className={`${concatPaymentMethodWithSubType(
          paymentMethod,
          isPPCheckoutExperiment ? paymentMethodSubType : undefined
        )} ${
          selectedPaymentMethod ===
          concatPaymentMethodWithSubType(paymentMethod, paymentMethodSubType)
            ? 'active'
            : ''
        } ${isSurchargingEnabled ? 'surcharge' : ''}`}
        onClick={handlePaymentMethodTypeChange}
        data-cy={`${isMobile ? 'mobile' : 'desktop'}-payment-tab-${paymentMethod}`}
      >
        <div className="tab">
          <PlaceholderButton>
            <Icon
              width={40}
              height={24}
              customStyling={
                ['paypal_ppaam'].includes(paymentMethod) ? { width: '29px', marginTop: '-7px' } : {}
              }
            />
          </PlaceholderButton>
          {isSurchargingEnabled && (
            <div className="tab-labels">
              <span>
                <FormattedMessage id={formattedMessageId} defaultMessage={defaultMessage} />
              </span>
              {surchargeLabel && <span className="surcharge-fee-label">{surchargeLabel}</span>}
            </div>
          )}
        </div>
        {!isSurchargingEnabled && (
          <FormattedMessage id={formattedMessageId} defaultMessage={defaultMessage} />
        )}
      </li>

      {/*language=SCSS*/}

      <style jsx>{`
        li {
          text-align: center;
          min-height: 81px;
          flex: 1;

          .tab {
            display: flex;
            justify-content: center;
            border-radius: 4px;
            min-height: 50px;
            min-width: 78px;
            border: 1px solid ${colors.gray07};
            user-select: none;
            cursor: pointer;
            margin: 8px 0;
          }

          .tab:hover {
            background-color: ${colors.whiteGreen};
          }

          :global(span) {
            font-family: AvenirNextforINTUIT-Regular;
            font-size: ${fontSize.xs};
            color: ${colors.gray};
            white-space: nowrap;
          }
        }

        :global(li.surcharge) {
          min-height: 0;
          display: flex;
          flex-direction: column;

          .tab {
            display: flex;
            box-sizing: border-box;
            flex: 1;
            padding: 8px 16px;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 4px;
            border-radius: 3px;
            border: 1px solid ${colors.gray05};
            background: #fff;
            min-height: 50px;
            min-width: 78px;
            margin: 8px 0;
          }

          .tab-labels {
            display: flex;
            flex-direction: column;
            align-items: center;
          }

          .surcharge-fee-label {
            font-size: 12px;
            color: ${colors.gray03};
            font-family: AvenirNextforINTUIT-Regular;
          }
        }

        :global(li.active) {
          .tab {
            border-top: 4px solid ${colors.green03};
          }
          :global(span) {
            font-family: AvenirNextforINTUIT-Demi;
            font-size: ${fontSize.sm};
          }
        }

        :global(li.active.surcharge) {
          .tab {
            border-top: 4px solid ${colors.green03};
            padding: 8px 16px;
          }
          :global(.surcharge-fee-label span),
          :global(.surcharge-fee-label) {
            font-family: AvenirNextforINTUIT-Regular;
            font-size: 12px;
          }
        }

        li:not(:first-child) {
          @media screen and (min-width: ${breakpoints.md}) {
            margin-left: 8px;
          }
        }
      `}</style>
    </>
  );
};

export default memo(PaymentMethodTab);
